<template>
  <Teleport to="body">
    <v-navigation-drawer
      :model-value="modelValue"
      location="right"
      temporary
      :width="drawerWidth"
      class="ci-drawer"
      @update:model-value="onDrawerUpdate"
    >
      <div v-if="call" class="ci-drawer__inner">
        <header class="ci-header">
          <div class="ci-header__brand">
            <div class="ci-header__icon" aria-hidden="true">
              <v-icon icon="mdi-brain" size="22" color="white" />
            </div>
            <div class="ci-header__titles">
              <h2 class="ci-header__title">Conversation intelligence</h2>
              <p class="ci-header__subtitle">{{ headerSubtitle }}</p>
            </div>
          </div>
          <button
            type="button"
            class="ci-header__close"
            aria-label="Close"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="ci-body">
          <section class="ci-card" aria-labelledby="ci-overview-heading">
            <h3 id="ci-overview-heading" class="ci-card__label">Call overview</h3>
            <dl class="ci-overview">
              <div
                v-for="item in overviewItems"
                :key="item.label"
                class="ci-overview__item"
              >
                <dt class="ci-overview__label">{{ item.label }}</dt>
                <dd class="ci-overview__value">
                  <span
                    v-if="item.chip && item.value !== '—'"
                    class="ci-chip ci-chip--success"
                  >
                    {{ item.value }}
                  </span>
                  <span v-else>{{ item.value }}</span>
                </dd>
              </div>
            </dl>
          </section>

          <section class="ci-card" aria-labelledby="ci-recording-heading">
            <h3 id="ci-recording-heading" class="ci-card__label">Voice recording</h3>

            <div v-if="recordingS3Key" class="ci-player">
              <div
                class="ci-player__wave"
                :class="{ 'ci-player__wave--active': isPlaying }"
                aria-hidden="true"
              >
                <span
                  v-for="(height, index) in waveformBars"
                  :key="index"
                  class="ci-player__bar"
                  :style="{ '--ci-bar-scale': height }"
                />
              </div>

              <div class="ci-player__controls">
                <button
                  type="button"
                  class="ci-player__play"
                  :disabled="recordingLoading || !playbackUrl || Boolean(recordingError)"
                  :aria-label="isPlaying ? 'Pause recording' : 'Play recording'"
                  @click="togglePlayback"
                >
                  <v-icon :icon="isPlaying ? 'mdi-pause' : 'mdi-play'" size="20" />
                </button>

                <div class="ci-player__track">
                  <input
                    type="range"
                    class="ci-player__slider"
                    min="0"
                    :max="duration || 0"
                    step="0.1"
                    :value="currentTime"
                    :disabled="!duration || recordingLoading || !playbackUrl"
                    aria-label="Recording progress"
                    @input="seekAudio"
                  />
                  <div class="ci-player__times">
                    <span>{{ formatPlayerTime(currentTime) }}</span>
                    <span>{{ formatPlayerTime(duration) }}</span>
                  </div>
                </div>
              </div>

              <p v-if="recordingLoading" class="ci-player__status">Loading recording…</p>
              <p v-else-if="recordingError" class="ci-player__status ci-player__status--error">
                {{ recordingError }}
              </p>

              <audio
                ref="audioRef"
                class="ci-player__audio"
                preload="metadata"
                :src="playbackUrl || undefined"
                @timeupdate="onTimeUpdate"
                @loadedmetadata="onLoadedMetadata"
                @ended="onAudioEnded"
                @error="onAudioError"
              />
            </div>

            <p v-else class="ci-empty-note">No recording available for this call.</p>
          </section>

          <section class="ci-card" aria-labelledby="ci-transcript-heading">
            <h3 id="ci-transcript-heading" class="ci-card__label">Transcript</h3>

            <div
              v-if="chatLines.length"
              ref="transcriptEl"
              class="ci-transcript"
              role="log"
              aria-label="Call transcript"
            >
              <div
                v-for="line in chatLines"
                :key="line.id"
                class="ci-message"
                :class="line.role === 'user' ? 'ci-message--user' : 'ci-message--agent'"
              >
                <div class="ci-message__avatar" aria-hidden="true">
                  <v-icon :icon="line.icon" size="16" />
                </div>
                <div class="ci-message__content">
                  <div class="ci-message__meta">
                    <span class="ci-message__sender">{{ line.senderLabel }}</span>
                    <time v-if="line.timeLabel" class="ci-message__time">
                      {{ line.timeLabel }}
                    </time>
                  </div>
                  <p class="ci-message__bubble">{{ line.text }}</p>
                </div>
              </div>
            </div>

            <p v-else class="ci-empty-note">
              No transcript available for this call yet.
            </p>
          </section>

          <section class="ci-card" aria-labelledby="ci-feedback-heading">
            <h3 id="ci-feedback-heading" class="ci-card__label">How did this call go?</h3>

            <div v-if="feedbackSource === 'AI'" class="ci-feedback-ai-badge">
              <v-icon icon="mdi-robot-outline" size="14" />
              AI flagged this call -- review and adjust if needed.
            </div>

            <div class="ci-feedback-rate">
              <button
                type="button"
                class="ci-feedback-btn ci-feedback-btn--good"
                :class="{ 'ci-feedback-btn--active': feedbackRating === 'good' }"
                @click="selectFeedbackRating('good')"
              >
                <v-icon icon="mdi-thumb-up-outline" size="18" />
                Good
              </button>
              <button
                type="button"
                class="ci-feedback-btn ci-feedback-btn--bad"
                :class="{ 'ci-feedback-btn--active': feedbackRating === 'needs_review' }"
                @click="selectFeedbackRating('needs_review')"
              >
                <v-icon icon="mdi-thumb-down-outline" size="18" />
                Needs review
              </button>
            </div>

            <v-textarea
              v-if="feedbackRating === 'needs_review'"
              v-model="feedbackComment"
              placeholder="What went wrong with the agent's behavior on this call?"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              rows="3"
              auto-grow
              class="ci-feedback-comment"
            />

            <p v-if="feedbackError" class="ci-feedback-error">{{ feedbackError }}</p>
            <p v-if="feedbackSubmittedLabel" class="ci-feedback-submitted">{{ feedbackSubmittedLabel }}</p>

            <v-btn
              v-if="feedbackRating"
              color="primary"
              size="small"
              class="text-none"
              rounded="lg"
              :loading="feedbackSubmitting"
              @click="submitFeedback"
            >
              Submit feedback
            </v-btn>
          </section>
        </div>
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<script setup>
import { computed, ref, toRef, watch, nextTick, onBeforeUnmount } from 'vue'
import { useDisplay } from 'vuetify'
import { useCallIntelligence } from '@/composables/callHistory/useCallIntelligence'
import { fetchCallFeedback, submitCallFeedback as submitCallFeedbackApi } from '@/api/callHistoryApi'

defineOptions({ name: 'CallIntelligenceDrawer' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  call: { type: Object, default: null },
  organization: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue'])

const display = useDisplay()
const transcriptEl = ref(null)

const drawerWidth = computed(() =>
  display.mobile.value ? '100%' : Math.min(520, display.width.value),
)

const callRef = toRef(props, 'call')
const orgRef = toRef(props, 'organization')
const isOpen = toRef(props, 'modelValue')

const {
  audioRef,
  isPlaying,
  currentTime,
  duration,
  recordingS3Key,
  playbackUrl,
  recordingLoading,
  recordingError,
  chatLines,
  headerSubtitle,
  overviewItems,
  waveformBars,
  formatPlayerTime,
  togglePlayback,
  seekAudio,
  onTimeUpdate,
  onLoadedMetadata,
  onAudioEnded,
  onAudioError,
  pausePlayback,
} = useCallIntelligence(callRef, orgRef, isOpen)

const feedbackRating = ref(null)
const feedbackComment = ref('')
const feedbackSubmitting = ref(false)
const feedbackError = ref('')
const feedbackSubmittedLabel = ref('')
const feedbackSource = ref('')

function resetFeedbackState() {
  feedbackRating.value = null
  feedbackComment.value = ''
  feedbackSubmitting.value = false
  feedbackError.value = ''
  feedbackSubmittedLabel.value = ''
  feedbackSource.value = ''
}

function selectFeedbackRating(rating) {
  feedbackRating.value = rating
  feedbackError.value = ''
}

async function submitFeedback() {
  const callId = props.call?.id
  const orgId = props.organization?.org_id
  if (!callId || !orgId || !feedbackRating.value) return

  if (feedbackRating.value === 'needs_review' && !feedbackComment.value.trim()) {
    feedbackError.value = 'Please add a comment about what went wrong.'
    return
  }

  feedbackSubmitting.value = true
  feedbackError.value = ''
  const response = await submitCallFeedbackApi({
    callId,
    orgId,
    rating: feedbackRating.value,
    comment: feedbackComment.value.trim(),
  })
  feedbackSubmitting.value = false

  if (response?.status === 'SUCCESS') {
    feedbackSubmittedLabel.value = 'Feedback submitted -- thank you.'
    feedbackSource.value = 'HUMAN'
  } else {
    feedbackError.value = response?.message || 'Could not submit feedback. Please try again.'
  }
}

async function loadExistingFeedback(callId) {
  if (!callId) return
  const response = await fetchCallFeedback(callId)
  const existing = response?.feedback
  if (existing) {
    feedbackRating.value = existing.rating || null
    feedbackComment.value = existing.comment || ''
    feedbackSource.value = existing.source || 'HUMAN'
    feedbackSubmittedLabel.value =
      existing.source === 'AI'
        ? 'AI already analyzed this call -- confirm or adjust below.'
        : 'Feedback already submitted for this call.'
  }
}

watch(
  () => props.call?.id,
  (callId) => {
    resetFeedbackState()
    if (callId && props.modelValue) loadExistingFeedback(callId)
  },
)

const CI_DRAWER_OPEN_CLASS = 'ci-drawer-open'

function setDrawerOpenState(open) {
  const root = document.documentElement
  if (!root) return
  if (open) {
    root.classList.add(CI_DRAWER_OPEN_CLASS)
  } else {
    root.classList.remove(CI_DRAWER_OPEN_CLASS)
  }
}

function close() {
  pausePlayback()
  emit('update:modelValue', false)
}

function onDrawerUpdate(value) {
  if (!value) pausePlayback()
  emit('update:modelValue', value)
}

watch(
  () => props.modelValue,
  (open) => {
    setDrawerOpenState(open)
    if (open) {
      nextTick(() => {
        const el = transcriptEl.value
        if (el) el.scrollTop = 0
      })
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  setDrawerOpenState(false)
})
</script>
